import {SearchFilterPredefined} from '../models/SearchFilterPredefined';
import {archiveFileTypeResource} from '../components/ObjectTypePicker/resources/ArchiveFileTypeResource';
import {audioFileTypeResource} from '../components/ObjectTypePicker/resources/AudioFileTypeResource';
import {documentFileTypeResource} from '../components/ObjectTypePicker/resources/DocumentFileTypeResource';
import {imageFileTypeResource} from '../components/ObjectTypePicker/resources/ImageFileTypeResource';
import {presentationFileTypeResource} from '../components/ObjectTypePicker/resources/PresentationFileTypeResource';
import {sheetFileTypeResource} from '../components/ObjectTypePicker/resources/SheetFileTypeResource';
import {videoFileTypeResource} from '../components/ObjectTypePicker/resources/VideoFileTypeResource';
import {otherFileTypeResource} from '../components/ObjectTypePicker/resources/OtherFileTypeResource';
import {getEmptySearchFilterPredefined, getSortTypeByKey} from './DtoUtils';
import {objectTypeOptionResource} from '../components/ObjectTypePicker/resources/ObjectTypeResource';
import {BreadcrumbPaths} from '../components/Breadcrumb/Breadcrumb';
import {convertToStringArray, isNullOrEmpty} from './HelperUtils';
import {SEARCH_TERM_MINIMAL_CHARACTERS_LENGTH} from '../init/constants';
import {SELECT_TYPE, TYPE_ALL} from './DropdownOptionPropsUtils';
import {SortType} from '../models/SortType';
import {SortOrder} from '../models/SortOrder';
import {defaultPredefinedFilters} from '../init/ui_component_defaults';

export const isInResource = (entity: string, resource: { value: string }[]): boolean => {
    if (isNullOrEmpty(entity)) {
        return false;
    }

    return resource.some(option => (option.value === entity.trim()));
};

export const isInAnyResource = (entity: string, resources: { value: string }[][]): boolean => {
    if (isNullOrEmpty(entity)) {
        return false;
    }

    return resources.some(resource => resource.some(option => (option.value === entity.trim())));
};

export const fileTypeResources = [
    archiveFileTypeResource,
    audioFileTypeResource,
    documentFileTypeResource,
    imageFileTypeResource,
    presentationFileTypeResource,
    sheetFileTypeResource,
    videoFileTypeResource,
    otherFileTypeResource
];

export const createSearchFilterPredefined = (searchParam: string | null, fileTypes: string[] | null, objectType: string | null): SearchFilterPredefined => {
    let searchFilterPredefined = getEmptySearchFilterPredefined();
    if ((searchParam !== undefined) && searchParam && (!isNullOrEmpty(searchParam.trim()))) {
        searchFilterPredefined.searchParam = searchParam.trim()
    }

    if ((objectType !== undefined) && objectType && isInResource(objectType, objectTypeOptionResource)) {
        searchFilterPredefined.objectType = objectType.toLowerCase();
    }

    if ((fileTypes !== undefined) && fileTypes && (fileTypes.length > 0)) {
        searchFilterPredefined.fileTypes = convertToStringArray(fileTypes).filter(entity => isInAnyResource(entity, fileTypeResources));
    }

    return searchFilterPredefined;
};

export const getSearchFilterPredefined = (predefinedFilters: SearchFilterPredefined): SearchFilterPredefined => {
    return createSearchFilterPredefined(predefinedFilters.searchParam, predefinedFilters.fileTypes, predefinedFilters.objectType);
};

export const isPredefinedFilterApplicable = (predefinedFilters: SearchFilterPredefined): boolean => {
    return ((predefinedFilters.searchParam && (!isNullOrEmpty(predefinedFilters.searchParam)))
        || (predefinedFilters.fileTypes && (predefinedFilters.fileTypes.length > 0))
        || ((!isNullOrEmpty(predefinedFilters.objectType)) && predefinedFilters.objectType && (TYPE_ALL !== predefinedFilters.objectType.toLowerCase()))) as boolean;
};

export const isSearchFilterApplied = (searchTerm: string | null, recentlyAdded: number, isFavorite: boolean, isPublic: boolean,
                                      isValidSortOrder: boolean, objectType: string | null, fileTypes: string[] | null): boolean => {
    return ((!isNullOrEmpty(searchTerm))
        || (recentlyAdded > 0)
        || (isFavorite)
        || (isPublic)
        || (isValidSortOrder)
        || ((!isNullOrEmpty(objectType)) && objectType && (TYPE_ALL !== objectType.toLowerCase()))
        || (fileTypes && (fileTypes.length > 0))) as boolean;
};

export const getBreadcrumbState = (searchTerm: string | null, recentlyAdded: number, isFavorite: boolean, isPublic: boolean,
                                   isValidSortOrder: boolean, objectType: string | null, fileTypes: string[] | null): BreadcrumbPaths => {
    if (isSearchFilterApplied(searchTerm, recentlyAdded, isFavorite, isPublic, isValidSortOrder, objectType, fileTypes)) {
        return BreadcrumbPaths.FILTERED;
    }

    return BreadcrumbPaths.HOME;
};

export const validateSearchTerm = (searchTerm: string | null | undefined): boolean => {
    let searchTermValue = searchTerm ?? '';
    return ((isNullOrEmpty(searchTermValue)) || ((searchTermValue.trim()).length >= SEARCH_TERM_MINIMAL_CHARACTERS_LENGTH)) as boolean;
};

export const validateObjectType = (objectType: string | null): boolean => {
    return (objectType && (TYPE_ALL !== objectType.toLowerCase())) as boolean;
};

export const validateFileTypes = (fileTypes: string[] | null): boolean => {
    return (fileTypes && (fileTypes.length >= 1)
        && (!((fileTypes.length === 1) && (isNullOrEmpty(fileTypes[0]) || (SELECT_TYPE === fileTypes[0]))))) as boolean;
};

export const validateSortOrder = (sortType: string, sortTypeDefault: string, sortOrder: string, sortOderDefault: string): boolean => {
    let sortTypeStatus = (isNullOrEmpty(sortType) && (!isNullOrEmpty(sortTypeDefault)) && (sortTypeDefault !== sortType)) ? sortTypeDefault : sortType;
    let sortOrderStatus = (isNullOrEmpty(sortOrder) && (!isNullOrEmpty(sortOderDefault)) && (sortOderDefault !== sortOrder)) ? sortOderDefault : sortOrder;
    if ((isNullOrEmpty(sortTypeStatus)) && isNullOrEmpty(sortOrderStatus)) {
        return false;
    }

    switch (getSortTypeByKey(sortTypeStatus)) {
        case SortType.UD:
            if (SortType.UD === getSortTypeByKey(sortType)) {
                return (sortOrderStatus && (SortOrder.DESC !== sortOrderStatus)) as boolean;
            }

            return (sortOrderStatus && (sortOderDefault !== sortOrderStatus)) as boolean;
        case SortType.AZ:
            if (SortType.AZ === getSortTypeByKey(sortType)) {
                return (sortOrderStatus && (SortOrder.ASC !== sortOrderStatus)) as boolean;
            }

            return (sortOrderStatus && (sortOderDefault !== sortOrderStatus)) as boolean;
        default:
            return false;
    }
};

export const convertToSearchFilterPredefined = (predefinedFilters: string): SearchFilterPredefined => {
    if ((isNullOrEmpty(predefinedFilters) || ('{}' === predefinedFilters))) {
        return defaultPredefinedFilters;
    }

    let predefinedFiltersDefault = JSON.parse(predefinedFilters) as SearchFilterPredefined;
    predefinedFiltersDefault.fileTypes = convertToStringArray(predefinedFiltersDefault.fileTypes);

    return predefinedFiltersDefault;
};
